/**
 * Hosted workspaces: the front door's "Open workspace" control (landing condition L1).
 *
 * Opening a workspace is a two-step hand-off:
 *
 * 1. `POST /v1/workspaces/{id}/handoff` from this page (same origin, session cookie,
 *    JSON). The front door answers `{handoff_url, token, delegation, expires_in}` with
 *    `Cache-Control: no-store`.
 * 2. A hidden `<form method="POST">` to `handoff_url`, carrying exactly two fields,
 *    `token` and `delegation`, submitted as a top-level navigation. The browser then
 *    sends `Origin: <front door>`, `Sec-Fetch-Mode: navigate` and
 *    `Sec-Fetch-Dest: document`, which is what the workspace's verifier requires.
 *
 * Neither credential is ever put in a URL, stored, or logged. The form must not carry
 * `rel="noreferrer"`: that sets the request's referrer policy to `no-referrer`, and the
 * browser then sends `Origin: null`, which the workspace refuses.
 *
 * The same trap applies to the page-wide `Referrer-Policy` response header: the hidden
 * form inherits the document's referrer policy, so a front-door policy of `no-referrer`
 * also yields `Origin: null` (F-57, 2026-10-09). The front door must serve
 * `strict-origin-when-cross-origin` (origin only cross-site, never the path). That header
 * is set in the host Caddyfile's `(security_headers)` snippet, which is not versioned in
 * this repo, so keep it when re-rendering the host Caddyfile.
 */

import { ApiClientError, requestJson, responseArray, responseRecord } from './http';

export { ApiClientError as WorkspacesApiError };

/** The only fields the workspace hand-off accepts (and the only ones this control sends). */
export const HANDOFF_FORM_FIELDS = Object.freeze(['token', 'delegation'] as const);

/** The workspace's hand-off path (`hosted_workspace_session.HANDOFF_PATH`). */
export const WORKSPACE_HANDOFF_PATH = '/api/workspace-session/handoff';

export type WorkspaceState = 'created' | 'active' | 'sleeping' | 'suspended';

/** Where the server's reconcile job stands for a workspace that is still being set up (F-54). */
export type WorkspaceSetupStage = 'queued' | 'working' | 'retrying' | 'failed' | 'not_queued' | 'unknown';
/** The server's closed, customer-safe reason vocabulary (`workspace_setup_status.SETUP_REASONS`). */
export type WorkspaceSetupReason = 'host_at_capacity' | 'not_entitled' | 'workspace_unhealthy' | 'setup_unavailable';

export interface WorkspaceSetup {
  stage: WorkspaceSetupStage;
  /** Whole seconds since the setup job was queued, on the server's clock. */
  age_seconds: number;
  queued_at: string | null;
  attempts: number;
  max_attempts: number;
  reason: WorkspaceSetupReason | null;
}

export interface Workspace {
  workspace_id: string;
  state: WorkspaceState | string;
  ingress_host: string;
  /** Why the workspace is not ready yet, when the server could read its setup job (see `setupFrom`). */
  setup?: WorkspaceSetup;
}

const SETUP_STAGES: readonly string[] = ['queued', 'working', 'retrying', 'failed', 'not_queued', 'unknown'];
const SETUP_REASONS: readonly string[] = ['host_at_capacity', 'not_entitled', 'workspace_unhealthy', 'setup_unavailable'];

function wholeCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}

/** A setup status the page can trust, or `undefined` (an unknown or malformed one is never guessed at). */
export function setupFrom(value: unknown): WorkspaceSetup | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const row = value as Record<string, unknown>;
  if (typeof row.stage !== 'string' || !SETUP_STAGES.includes(row.stage)) return undefined;
  if (!wholeCount(row.age_seconds) || !wholeCount(row.attempts) || !wholeCount(row.max_attempts)) return undefined;
  if (!(row.reason === null || (typeof row.reason === 'string' && SETUP_REASONS.includes(row.reason)))) return undefined;
  if (!(row.queued_at === null || typeof row.queued_at === 'string')) return undefined;
  return {
    stage: row.stage as WorkspaceSetupStage,
    age_seconds: row.age_seconds,
    queued_at: row.queued_at as string | null,
    attempts: row.attempts,
    max_attempts: row.max_attempts,
    reason: row.reason as WorkspaceSetupReason | null,
  };
}

export interface WorkspaceHandoff {
  handoff_url: string;
  token: string;
  delegation: string;
  expires_in: number;
}

const WORKSPACE_ID = /^wsp_[0-9a-f]{32}$/;

function workspaceFrom(value: unknown): Workspace {
  const record = responseRecord(value);
  for (const field of ['workspace_id', 'state', 'ingress_host']) {
    if (typeof record[field] !== 'string') {
      throw new ApiClientError('malformed', 200, 'malformed_response', `workspace.${field} must be a string`);
    }
  }
  if (!WORKSPACE_ID.test(record.workspace_id as string)) {
    throw new ApiClientError('malformed', 200, 'malformed_response', 'workspace.workspace_id is malformed');
  }
  const workspace: Workspace = {
    workspace_id: record.workspace_id as string,
    state: record.state as string,
    ingress_host: record.ingress_host as string,
  };
  // F-54: a `created` row; a `sleeping` one whose start is retrying or has failed; an `active` one a setup
  // job still holds (the server decides which rows carry it).
  const setup = ['created', 'sleeping', 'active'].includes(record.state as string) ? setupFrom(record.setup) : undefined;
  if (setup) workspace.setup = setup;
  return workspace;
}

/** Request the caller's first anchor. Identity, grant and plan come only from the server. */
export async function firstOpenWorkspace(): Promise<void> {
  const payload = responseRecord(await requestJson('/v1/workspaces/first-open', {
    method: 'POST', body: JSON.stringify({}),
  }));
  if (typeof payload.workspace_id !== 'string' || !WORKSPACE_ID.test(payload.workspace_id)
      || typeof payload.state !== 'string' || typeof payload.created !== 'boolean') {
    throw new ApiClientError('malformed', 200, 'malformed_response', 'workspace setup response is malformed');
  }
}

/** `GET /v1/workspaces`: the signed-in organization's workspaces (id, state, ingress host, setup status). */
export async function listWorkspaces(): Promise<Workspace[]> {
  const payload = await requestJson('/v1/workspaces');
  return responseArray(payload, 'workspaces').map(workspaceFrom);
}

function malformed(message: string): ApiClientError {
  return new ApiClientError('malformed', 200, 'malformed_response', message);
}

/**
 * The hand-off URL must be exactly `https://<expectedHost>/api/workspace-session/handoff`, where
 * `expectedHost` is the ingress host `GET /v1/workspaces` listed for this workspace and itself starts
 * with `wsp-<this workspace's 32 hex>.`: no credentials, port, query or fragment. A response that names
 * any other place (another workspace, a look-alike `wsp-<id>.attacker.example`) is refused before a
 * credential leaves this page.
 */
export function checkedHandoffUrl(raw: unknown, workspaceId: string, expectedHost: string): string {
  if (typeof raw !== 'string') throw malformed('handoff_url must be a string');
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    throw malformed('handoff_url is not a URL');
  }
  const label = `wsp-${workspaceId.slice('wsp_'.length)}.`;
  const host = String(expectedHost || '').toLowerCase();
  if (!host.startsWith(label) || host.length <= label.length) {
    throw malformed('the listed ingress host is not this workspace\'s');
  }
  if (
    url.hostname !== host
    || url.protocol !== 'https:'
    || url.username !== ''
    || url.password !== ''
    || url.port !== ''
    || url.search !== ''
    || url.hash !== ''
    || url.pathname !== WORKSPACE_HANDOFF_PATH
    || !url.hostname.startsWith(label)
    || url.hostname.length <= label.length
    || raw !== `https://${url.hostname}${WORKSPACE_HANDOFF_PATH}`
  ) {
    throw malformed('handoff_url is not this workspace\'s hand-off address');
  }
  return raw;
}

/** Step 1: ask the front door for a single-use hand-off (60 s) and its relay delegation. */
export async function requestWorkspaceHandoff(workspaceId: string, expectedHost: string): Promise<WorkspaceHandoff> {
  if (!WORKSPACE_ID.test(workspaceId)) {
    throw new ApiClientError('malformed', 0, 'invalid_workspace_id', 'the workspace id is malformed');
  }
  const payload = await requestJson(`/v1/workspaces/${encodeURIComponent(workspaceId)}/handoff`, {
    method: 'POST',
    // The issuer reads no body; JSON keeps the request on the API's JSON-only state-change guard.
    body: '{}',
  });
  const record = responseRecord(payload);
  if (typeof record.token !== 'string' || !record.token) throw malformed('the hand-off token is missing');
  if (typeof record.delegation !== 'string' || !record.delegation) throw malformed('the relay delegation is missing');
  return {
    handoff_url: checkedHandoffUrl(record.handoff_url, workspaceId, expectedHost),
    token: record.token,
    delegation: record.delegation,
    expires_in: typeof record.expires_in === 'number' ? record.expires_in : 0,
  };
}

/** The part of `document` the hand-off form needs (a seam for tests; production passes `document`). */
export interface HandoffDocument {
  createElement(tagName: 'form'): HTMLFormElement;
  createElement(tagName: 'input'): HTMLInputElement;
  body: { appendChild(node: HTMLFormElement): unknown };
}

/**
 * Step 2's form: `POST`, `application/x-www-form-urlencoded`, `action` = the checked hand-off URL,
 * target `_self`, and exactly the two hidden inputs of {@link HANDOFF_FORM_FIELDS}.
 */
export function buildHandoffForm(handoff: WorkspaceHandoff, doc: HandoffDocument): HTMLFormElement {
  const form = doc.createElement('form');
  form.method = 'POST';
  form.action = handoff.handoff_url;
  form.enctype = 'application/x-www-form-urlencoded';
  form.target = '_self';
  form.style.display = 'none';
  const values: Record<(typeof HANDOFF_FORM_FIELDS)[number], string> = {
    token: handoff.token,
    delegation: handoff.delegation,
  };
  for (const name of HANDOFF_FORM_FIELDS) {
    const input = doc.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = values[name];
    form.appendChild(input);
  }
  return form;
}

/**
 * Open a workspace: fetch the hand-off, then navigate this tab to the workspace with a form POST.
 * `expectedHost` is the `ingress_host` {@link listWorkspaces} returned for this workspace; the
 * hand-off URL must name exactly that host. Resolves once the navigation has been started; rejects
 * (with an {@link ApiClientError}) if the front door refuses or the hand-off URL does not match, in
 * which case nothing has been sent to the workspace.
 */
export async function openWorkspace(
  workspaceId: string,
  expectedHost: string,
  doc: HandoffDocument = globalThis.document as unknown as HandoffDocument,
  /** Checked after the hand-off arrives: a page the person has left never navigates (F-53 auto-open). */
  stillWanted: () => boolean = () => true,
): Promise<void> {
  const handoff = await requestWorkspaceHandoff(workspaceId, expectedHost);
  if (!stillWanted()) throw new ApiClientError('http', 0, 'open_cancelled', 'the page was left before the workspace opened');
  const form = buildHandoffForm(handoff, doc);
  doc.body.appendChild(form);
  form.submit();
}

/**
 * "Allow my workspace to use this key" (relay contract K-12.3).
 *
 * A hosted workspace never holds a provider key; the relay spends a key for it only
 * when the customer has allowed that workspace to use that key. These calls list,
 * grant and revoke those standing approvals. They carry key ids only, never key
 * material.
 */
export interface SpendApproval {
  approval_id: string;
  workspace_id: string;
  key_id: string;
  approved_at: string;
}

function spendApprovalFrom(value: unknown): SpendApproval {
  const record = responseRecord(value);
  for (const field of ['approval_id', 'workspace_id', 'key_id', 'approved_at']) {
    if (typeof record[field] !== 'string') throw malformed(`approval.${field} must be a string`);
  }
  return {
    approval_id: record.approval_id as string,
    workspace_id: record.workspace_id as string,
    key_id: record.key_id as string,
    approved_at: record.approved_at as string,
  };
}

function approvalsPath(workspaceId: string): string {
  if (!WORKSPACE_ID.test(workspaceId)) throw malformed('workspace id is malformed');
  return `/v1/workspaces/${workspaceId}/spend-approvals`;
}

/** `GET /v1/workspaces/{id}/spend-approvals`: the keys this workspace may use. */
export async function listSpendApprovals(workspaceId: string): Promise<SpendApproval[]> {
  return responseArray(await requestJson(approvalsPath(workspaceId)), 'approvals').map(spendApprovalFrom);
}

/** Allow the workspace to use one stored key. */
export async function allowWorkspaceKey(workspaceId: string, keyId: string): Promise<SpendApproval> {
  const payload = await requestJson(approvalsPath(workspaceId), {
    method: 'POST',
    body: JSON.stringify({ key_id: keyId }),
  });
  return spendApprovalFrom(responseRecord(payload).approval);
}

/** Stop the workspace using a key. Its next call for that key is refused. */
export async function stopWorkspaceKey(workspaceId: string, approvalId: string): Promise<void> {
  await requestJson(`${approvalsPath(workspaceId)}/${encodeURIComponent(approvalId)}/revoke`, { method: 'POST' });
}

/** Customer-set media limits use the existing front-door session, never a workspace credential. */
export const MEDIA_LIMIT_MAX_UNITS = 2_000_000_000;
export type MediaUnitKind = 'image' | 'video_second' | 'audio_character' | 'audio_second' | 'job';
export interface MediaOperation {
  provider: string;
  operation: string;
  unit_kind: MediaUnitKind;
}
export interface MediaLimit extends MediaOperation {
  limit_units: number;
  window_seconds: number;
  set_at: string;
}
export interface MediaUsage extends MediaOperation {
  used_units: number;
  window_seconds: number;
  limit_units: number | null;
  continue_live: boolean;
}
export interface WorkspaceMediaLimits {
  limits: MediaLimit[];
  limitable: MediaOperation[];
}
const MEDIA_UNITS = new Set(['image', 'video_second', 'audio_character', 'audio_second', 'job']);

function wholeMatch(value: unknown, pattern: RegExp): value is string {
  return typeof value === 'string' && pattern.exec(value)?.[0] === value;
}
function mediaWorkspacePath(workspaceId: string): string {
  if (!wholeMatch(workspaceId, /^wsp_[0-9a-f]{32}$/)) throw malformed('workspace id is malformed');
  return `/v1/workspaces/${workspaceId}`;
}
function mediaOperationPath(workspaceId: string, provider: string, operation: string): string {
  const base = mediaWorkspacePath(workspaceId);
  if (!wholeMatch(provider, /^[a-z][a-z0-9_-]*$/)
      || !wholeMatch(operation, /^[a-z0-9_-]+(?:\/[a-z0-9_-]+)*$/)) {
    throw malformed('media operation is malformed');
  }
  return `${base}/media-limits/${encodeURIComponent(provider)}/${operation.split('/').map(encodeURIComponent).join('/')}`;
}
function mediaOperationFrom(value: unknown): MediaOperation {
  const row = responseRecord(value);
  if (!wholeMatch(row.provider, /^[a-z][a-z0-9_-]*$/)
      || !wholeMatch(row.operation, /^[a-z0-9_-]+(?:\/[a-z0-9_-]+)*$/)
      || typeof row.unit_kind !== 'string' || !MEDIA_UNITS.has(row.unit_kind)) {
    throw malformed('media operation response is malformed');
  }
  return { provider: row.provider, operation: row.operation, unit_kind: row.unit_kind as MediaUnitKind };
}
function validMediaLimit(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 1 && value <= MEDIA_LIMIT_MAX_UNITS;
}
function mediaLimitFrom(value: unknown): MediaLimit {
  const row = responseRecord(value);
  const operation = mediaOperationFrom(row);
  if (!validMediaLimit(row.limit_units) || row.window_seconds !== 86400
      || typeof row.set_at !== 'string' || !Number.isFinite(Date.parse(row.set_at))) {
    throw malformed('media limit response is malformed');
  }
  return { ...operation, limit_units: row.limit_units, window_seconds: 86400, set_at: row.set_at };
}
function distinctMediaOperations<T extends MediaOperation>(rows: T[]): T[] {
  const seen = new Set<string>();
  for (const row of rows) {
    const key = `${row.provider}/${row.operation}`;
    if (seen.has(key)) throw malformed('duplicate media operation');
    seen.add(key);
  }
  return rows;
}

export async function listWorkspaceMediaLimits(workspaceId: string): Promise<WorkspaceMediaLimits> {
  const payload = await requestJson(`${mediaWorkspacePath(workspaceId)}/media-limits`);
  const limitable = distinctMediaOperations(responseArray(payload, 'limitable').map(mediaOperationFrom));
  const limits = distinctMediaOperations(responseArray(payload, 'limits').map(mediaLimitFrom));
  if (limits.some((row) => !limitable.some((spec) => spec.provider === row.provider
      && spec.operation === row.operation && spec.unit_kind === row.unit_kind))) {
    throw malformed('media limit is outside the server catalog');
  }
  return { limits, limitable };
}

export async function setWorkspaceMediaLimit(
  workspaceId: string, provider: string, operation: string, limitUnits: number,
): Promise<MediaLimit> {
  const path = mediaOperationPath(workspaceId, provider, operation);
  if (!validMediaLimit(limitUnits)) throw malformed('media limit must be a positive whole number');
  const payload = await requestJson(path, { method: 'PUT', body: JSON.stringify({ limit_units: limitUnits }) });
  const limit = mediaLimitFrom(responseRecord(payload).limit);
  if (limit.provider !== provider || limit.operation !== operation || limit.limit_units !== limitUnits) throw malformed('media limit request does not match');
  return limit;
}

export async function clearWorkspaceMediaLimit(workspaceId: string, provider: string, operation: string): Promise<boolean> {
  const payload = responseRecord(await requestJson(mediaOperationPath(workspaceId, provider, operation), { method: 'DELETE' }));
  if (typeof payload.cleared !== 'boolean') throw malformed('media limit clear response is malformed');
  return payload.cleared;
}

export async function readWorkspaceMediaUsage(workspaceId: string): Promise<MediaUsage[]> {
  const payload = await requestJson(`${mediaWorkspacePath(workspaceId)}/media-usage`);
  return distinctMediaOperations(responseArray(payload, 'usage').map((value): MediaUsage => {
    const row = responseRecord(value);
    const operation = mediaOperationFrom(row);
    if (typeof row.used_units !== 'number' || !Number.isSafeInteger(row.used_units) || row.used_units < 0
        || row.window_seconds !== 86400 || !(row.limit_units === null || validMediaLimit(row.limit_units))
        || typeof row.continue_live !== 'boolean') throw malformed('media usage response is malformed');
    return { ...operation, used_units: row.used_units, window_seconds: 86400,
      limit_units: row.limit_units, continue_live: row.continue_live };
  }));
}

export type MediaContinueScope = 'user_limit' | 'guard';
export interface MediaContinueGrant { scope: MediaContinueScope; provider: string; seconds: 900 }
/** Human front-door session only; this grants permission and never starts a media action. */
export async function grantWorkspaceMediaContinue(
  workspaceId: string, provider: string, scope: MediaContinueScope,
): Promise<MediaContinueGrant> {
  const path = `${mediaWorkspacePath(workspaceId)}/media-continue`;
  if (!wholeMatch(provider, /^[a-z][a-z0-9_-]*$/) || !['user_limit', 'guard'].includes(scope)) {
    throw malformed('media Continue request is malformed');
  }
  const payload = await requestJson(path, { method: 'POST', body: JSON.stringify({ scope, provider }) });
  const grant = responseRecord(responseRecord(payload).continue);
  if (grant.scope !== scope || grant.provider !== provider || grant.seconds !== 900) {
    throw malformed('media Continue could not be confirmed');
  }
  return { scope, provider, seconds: 900 };
}

/**
 * "Connect your AI tool" (walkthrough F-62): the workspace's `/mcp` address and its connection key.
 *
 * The key is generated on request and is in the response ONCE: the server keeps only a digest. Callers hold
 * it in component state, never in storage, a URL or a log, and drop it when the page is left.
 */
export function workspaceMcpEndpoint(ingressHost: string): string {
  return `https://${String(ingressHost || '').toLowerCase()}/mcp`;
}

/** The server stops and restarts the studio while it switches keys; the default 15 s would drop the only copy. */
export const MCP_KEY_TIMEOUT_MS = 240_000;

export async function generateMcpKey(workspaceId: string): Promise<string> {
  if (!WORKSPACE_ID.test(workspaceId)) throw malformed('workspace id is malformed');
  const payload = responseRecord(await requestJson(`/v1/workspaces/${workspaceId}/mcp-key`, {
    method: 'POST',
    body: '{}',
  }, MCP_KEY_TIMEOUT_MS));
  if (payload.workspace_id !== workspaceId || typeof payload.token !== 'string' || payload.token.length < 20) {
    throw malformed('the connection key response is malformed');
  }
  return payload.token;
}
